import { useRef, useState, useCallback } from "react";
import { Plus, Spinner } from "@phosphor-icons/react";

const ACCEPT_EXTS = [".pdf", ".png", ".jpg", ".jpeg", ".webp", ".bmp", ".tiff", ".tif", ".dwg", ".dxf"];

/**
 * Drag-and-drop / click-to-browse upload zone for the Analysis tool.
 * Handles multiple files sequentially to avoid slot race-conditions on the server.
 */
export default function UploadZone({ onUpload, uploading }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    for (const f of Array.from(files)) {
      await onUpload(f);
    }
  }, [onUpload]);

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
      onClick={() => !uploading && inputRef.current?.click()}
      className={`flex flex-col items-center justify-center gap-2 p-4 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
        dragging
          ? "border-primary bg-primary/5"
          : uploading
          ? "border-slate-200 bg-slate-50 cursor-not-allowed"
          : "border-slate-300 hover:border-primary/60 hover:bg-slate-50"
      }`}
      data-testid="analysis-upload-zone"
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_EXTS.join(",")}
        multiple
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
        disabled={uploading}
      />
      {uploading ? (
        <Spinner size={20} className="text-primary animate-spin" />
      ) : (
        <Plus size={20} className="text-slate-400" />
      )}
      <p className="text-xs text-slate-500 text-center">
        {uploading ? "Uploading..." : "Add drawing"}
      </p>
    </div>
  );
}
